import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CreditCard, Mail, Receipt, Download, Inbox, Pencil } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { ActionBar } from '@/components/ui/ActionBar';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Tooltip } from '@/components/ui/Tooltip';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { editCompany, loadCompanyDetail } from '@/store/actions/company';
import { apiService } from '../../../utils/api';
import { LICENCE } from '../../../constants/index';
import ChangeLicenceModal from './components/modals/ChangeLicenceModal';

const getInvoices = async (id) => {
  return await apiService.get(`api/v1/company/${id}/invoice/`);
};

const formatKc = (n) => `${new Intl.NumberFormat('cs-CZ').format(Number(n) || 0)} Kč`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ── Hlavička sekce (styl jako v TypeMarku) ──────────────────────
const SectionTitle = ({
  icon,
  title,
  subtitle,
  action,
}: {
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) => (
  <div className="flex items-center justify-between gap-3 py-3 border-b border-neutral-150">
    <div className="flex items-center gap-2.5 min-w-0">
      {icon}
      <div className="min-w-0">
        <div className="text-sm font-semibold text-neutral-900 tracking-tight leading-tight">
          {title}
        </div>
        {subtitle && (
          <div className="text-xs text-neutral-500 leading-tight mt-0.5 truncate">{subtitle}</div>
        )}
      </div>
    </div>
    {action}
  </div>
);

// ── Sekce: Licenční plán ────────────────────────────────────────
const PlanSection = ({ detail, onChangePlan }) => {
  const licenceType = detail?.licence?.licence_type;
  const isPaid = licenceType === 'pay';
  const planLabel = LICENCE[licenceType]?.description ?? 'Neuvedeno';

  const pricePerPerson = detail?.licence?.price_per_employee_no_vat;
  const monthly = detail?.licence?.monthly_price_no_vat;
  const validFrom = detail?.licence?.valid_from
    ? format(new Date(detail.licence.valid_from), 'M/yyyy')
    : null;
  const seats = pricePerPerson > 0 && monthly != null ? Math.round(monthly / pricePerPerson) : null;

  const rowCls = 'grid items-baseline gap-4 py-2.5 border-b border-neutral-100 last:border-0';
  const rowStyle = { gridTemplateColumns: '220px 1fr' } as React.CSSProperties;

  return (
    <section>
      <SectionTitle
        icon={
          <span
            className={cn(
              'inline-flex h-7 w-7 items-center justify-center rounded shrink-0',
              isPaid ? 'bg-primary-50 text-primary-600' : 'bg-neutral-100 text-neutral-500'
            )}
          >
            <CreditCard className="h-[15px] w-[15px]" />
          </span>
        }
        title="Licenční plán"
        subtitle={planLabel}
        action={
          <Button
            variant="outline"
            size="sm"
            data-testid="licence-company-change-plan-btn"
            onClick={onChangePlan}
          >
            <Pencil /> Změnit plán
          </Button>
        }
      />

      <div className="py-2 max-w-2xl">
        <div className={rowCls} style={rowStyle}>
          <div className="text-sm text-neutral-500">Aktuální plán</div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-neutral-900">{planLabel}</span>
            <Badge variant={isPaid ? 'brand' : 'default'} size="sm">
              {isPaid ? 'Aktivní' : 'Zdarma'}
            </Badge>
          </div>
        </div>
        <div className={rowCls} style={rowStyle}>
          <div className="text-sm text-neutral-500">Cena za osobu / měsíc</div>
          <div className="text-sm text-neutral-900 font-mono">
            {formatKc(pricePerPerson)} <span className="text-neutral-400">bez DPH</span>
          </div>
        </div>
        {seats != null && (
          <div className={rowCls} style={rowStyle}>
            <div className="text-sm text-neutral-500">Počet uživatelů</div>
            <div className="text-sm text-neutral-900 font-mono">{seats}</div>
          </div>
        )}
        <div className={rowCls} style={rowStyle}>
          <div className="text-sm text-neutral-500">
            Za období{validFrom ? ` ${validFrom}` : ''}
          </div>
          <div className="text-sm font-semibold text-neutral-900 font-mono">
            {formatKc(monthly)} <span className="font-normal text-neutral-400">bez DPH</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// ── Sekce: E-mail pro faktury ───────────────────────────────────
const InvoiceEmailSection = ({ email, setEmail, emailError }) => (
  <section>
    <SectionTitle title="E-mail pro zasílání faktur" />
    <div className="py-4 max-w-xl space-y-1.5">
      <Label htmlFor="invoice-email" className="normal-case tracking-normal">
        E-mail pro zasílání elektronických faktur
      </Label>
      <div className="relative">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-[15px] w-[15px] text-neutral-400 pointer-events-none" />
        <Input
          id="invoice-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="pl-9"
          placeholder="vas@email.cz"
          aria-invalid={!!emailError}
          data-testid="bill-form-invoicing-email-input"
        />
      </div>
      {emailError ? (
        <div className="text-xs text-danger">{emailError}</div>
      ) : (
        <div className="text-xs text-neutral-500">
          Daňové doklady posíláme na tuto adresu vždy k 5. dni v měsíci.
        </div>
      )}
    </div>
  </section>
);

// ── Sekce: Faktury ──────────────────────────────────────────────
const InvoicesSection = ({ invoices, onDownloadAll }) => {
  const count = invoices?.length ?? 0;
  const total = (invoices || []).reduce((s, i) => s + (Number(i.price) || 0), 0);

  const thCls =
    'text-left font-medium text-[11px] uppercase tracking-wider text-neutral-500 px-3 py-2.5';

  return (
    <section>
      <SectionTitle
        title="Faktury"
        action={
          count > 0 ? (
            <Button
              variant="secondary"
              size="sm"
              data-testid="licence-company-download-all-btn"
              onClick={onDownloadAll}
            >
              <Download /> Stáhnout vše
            </Button>
          ) : undefined
        }
      />

      <div className="py-4">
        {count === 0 ? (
          <div className="rounded border border-dashed border-neutral-200 bg-neutral-25/50 px-6 py-12 flex flex-col items-center text-center">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-neutral-100 text-neutral-400 mb-3">
              <Inbox className="h-5 w-5" />
            </span>
            <div className="text-sm font-medium text-neutral-800">Zatím žádné faktury</div>
            <div className="text-xs text-neutral-500 mt-1 max-w-[280px]">
              Na neplacené licenci se faktury nevystavují. Po přechodu na placený plán se zde objeví
              všechny daňové doklady.
            </div>
          </div>
        ) : (
          <>
            <div className="text-xs text-neutral-500 mb-3">
              <span className="font-medium text-neutral-700">{count}</span>{' '}
              {count === 1 ? 'faktura' : count < 5 ? 'faktury' : 'faktur'}
              <span className="text-neutral-300 mx-1.5">·</span>
              celkem <span className="font-mono text-neutral-700">{formatKc(total)}</span>
            </div>
            <div className="rounded border border-neutral-200 overflow-hidden bg-white">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-neutral-25 border-b border-neutral-200">
                    <th className={thCls}>Faktura</th>
                    <th className={thCls}>Vystaveno</th>
                    <th className={cn(thCls, 'text-right')}>Cena bez DPH</th>
                    <th className={cn(thCls, 'text-right')}>Cena s DPH</th>
                    <th className="w-12 px-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="group hover:bg-neutral-25 transition-colors">
                      <td className="px-3 py-2.5 align-middle">
                        <span className="inline-flex items-center gap-2">
                          <Receipt className="h-[14px] w-[14px] text-neutral-400 shrink-0" />
                          <span className="font-mono text-neutral-900">{inv.number}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2.5 align-middle text-neutral-700 whitespace-nowrap">
                        {inv.issue_date || 'Neuvedeno'}
                      </td>
                      <td className="px-3 py-2.5 align-middle text-right font-mono text-neutral-700 whitespace-nowrap">
                        {formatKc(inv.price_no_vat)}
                      </td>
                      <td className="px-3 py-2.5 align-middle text-right font-mono text-neutral-900 whitespace-nowrap">
                        {formatKc(inv.price)}
                      </td>
                      <td className="px-3 py-2.5 align-middle text-right">
                        {inv.document && (
                          <Tooltip content="Stáhnout PDF">
                            <a
                              href={inv.document}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`bill-list-download-link-${inv.id}`}
                              className="h-7 w-7 inline-flex items-center justify-center rounded text-neutral-400 hover:bg-primary-50 hover:text-primary-600 transition-colors opacity-60 group-hover:opacity-100"
                            >
                              <Download className="h-[14px] w-[14px]" />
                            </a>
                          </Tooltip>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

// ── Stránka: Předplatné a fakturace ─────────────────────────────
const Invoices = () => {
  const detail = useSelector((state) => state?.company?.detail);
  const dispatch = useDispatch();

  const [invoices, setInvoices] = useState([]);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [openChangeLicenceDialog, setOpenChangeLicenceDialog] = useState(false);

  useEffect(() => {
    if (!detail?.id) {
      dispatch(loadCompanyDetail());
    }
  }, []);

  useEffect(() => {
    setEmail(detail?.invoicing_email || '');
  }, [detail?.invoicing_email]);

  useEffect(() => {
    if (!detail?.id) return;

    const fetchData = async () => {
      try {
        const { data } = await getInvoices(detail.id);
        setInvoices(Array.isArray(data) ? data : []);
      } catch (e) {
        console.log(e);
      }
    };

    fetchData();
  }, [detail?.id]);

  const handleSave = () => {
    if (!EMAIL_RE.test(email.trim())) {
      setEmailError('Zadejte validní e-mail!');
      return;
    }
    setEmailError(null);
    dispatch(editCompany({ invoicing_email: email.trim() }));
    toast.success('Fakturační e-mail uložen');
  };

  const handleDownloadAll = () => {
    invoices.forEach((inv) => {
      if (inv.document) {
        window.open(inv.document, '_blank', 'noopener,noreferrer');
      }
    });
  };

  if (!detail?.id) {
    return (
      <div className="flex-1 flex justify-center items-center">
        <SpinnerOverlay size="lg" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <ActionBar
        left={<h3 className="text-sm font-semibold tracking-tight">Předplatné a fakturace</h3>}
        right={
          <Button data-testid="licence-company-save-btn" size="sm" onClick={handleSave}>
            Uložit
          </Button>
        }
      />

      <div className="px-8 py-5 space-y-2">
        <PlanSection detail={detail} onChangePlan={() => setOpenChangeLicenceDialog(true)} />
        <InvoiceEmailSection email={email} setEmail={setEmail} emailError={emailError} />
        <InvoicesSection invoices={invoices} onDownloadAll={handleDownloadAll} />
      </div>

      <ChangeLicenceModal
        visible={openChangeLicenceDialog}
        onClose={() => setOpenChangeLicenceDialog(false)}
      />
    </div>
  );
};

export default Invoices;
